// src/pages/AutomationV2/mappers/rule.mapper.ts

import { dtoConditionsToTree } from './conditions.mapper';

import type { CreateRuleBodyGeneral } from '../api/automationApi';
import type { AutomationRuleDTO, ConditionDTO, LogicalOp, Operator } from '../types/dto';
import type { RuleDraft, ConditionGroup, ConditionLeaf, ConditionNode } from '../types/rule';

// --------- DTO -> Draft ---------
export function dtoToDraft(dto: AutomationRuleDTO): RuleDraft {
  // ✅ USAR conditions.mapper.ts que TEM o achatamento!
  const root = dtoConditionsToTree(dto.conditions);
  return {
    id: dto.id,
    uuid: dto.uuid,
    name: dto.name,
    description: dto.description,
    isActive: dto.is_active,
    organization_uuid: dto.organization_uuid, // pode ser usado em edição
    created_by: dto.created_by,
    conditionsRoot: root,
    actions: [],
  };
}

// ❌ DEPRECATED: Usar dtoConditionsToTree do conditions.mapper.ts ao invés!
// Esta função não tem o achatamento hierárquico
function _buildTreeFromFlat_DEPRECATED(list: ConditionDTO[]): ConditionGroup | undefined {
  if (!list || list.length === 0) return undefined;

  console.log('🌳 === INÍCIO buildTreeFromFlat ===');
  console.log('📥 Lista de condições recebida:', list);

  // Agrupar por parent_id
  const byParent: Record<string, ConditionDTO[]> = {};
  for (const c of list) {
    const key = String(c.parent_id ?? 'root');
    (byParent[key] ??= []).push(c);
  }

  console.log('📊 Agrupamento por parent_id:', byParent);

  // Função recursiva para construir a árvore
  const attach = (parentServerId: number | null): ConditionNode[] => {
    const children = byParent[String(parentServerId ?? 'root')] ?? [];

    console.log(`🔗 Construindo filhos para parent_id ${parentServerId}:`, children);

    // Ordenar por order
    children.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

    const result = children.map(c => {
      if (c.is_group) {
        console.log(
          `📁 Criando grupo ${c.id} (UUID: ${c.uuid}) com logical_operator: ${c.logical_operator}`
        );
        return {
          id: c.uuid, // USAR UUID REAL DO BACKEND
          type: 'group',
          logical: (c.logical_operator ?? 'AND') as LogicalOp,
          children: attach(c.id),
        } as ConditionGroup;
      }

      // É uma condição (folha)
      console.log(`🍃 Criando condição ${c.id} (UUID: ${c.uuid}):`, {
        left_operand: c.left_operand,
        operator: c.operator,
        right_operand: c.right_operand,
      });

      return {
        id: c.uuid, // USAR UUID REAL DO BACKEND
        type: 'rule',
        left: c.left_operand ?? 'submission.option_id',
        operator: (c.operator ?? 'EQUALS') as Operator,
        right: c.right_operand ?? '',
        meta: {
          // Preservar informações adicionais se necessário
          field_uuid: extractFieldUuid(c.left_operand),
        },
      } as ConditionLeaf;
    });

    console.log(`✅ Resultado para parent_id ${parentServerId}:`, result);
    return result;
  };

  // Encontrar todas as raízes (grupos com parent_id = null)
  const rootCandidates = byParent['root'] ?? [];
  console.log('🌱 Candidatos a raiz:', rootCandidates);

  // Se há múltiplos grupos raiz, criar um wrapper
  if (rootCandidates.length > 1) {
    console.log('🔄 Múltiplas raízes encontradas, criando wrapper');
    return {
      id: `wrapper-${Date.now()}`, // ID temporário para wrapper
      type: 'group',
      logical: 'AND', // Operador padrão para múltiplas raízes
      children: attach(null),
    };
  }

  // Se há apenas uma raiz
  const rootServer = rootCandidates[0];
  if (!rootServer) {
    console.log('⚠️ Nenhuma raiz encontrada, criando raiz implícita');
    return {
      id: `implicit-root-${Date.now()}`, // ID temporário para raiz implícita
      type: 'group',
      logical: 'AND',
      children: attach(null),
    };
  }

  // Se a raiz é um grupo, usar ele
  if (rootServer.is_group) {
    console.log('🎯 Usando grupo raiz existente:', rootServer);
    return {
      id: rootServer.uuid, // USAR UUID REAL DO BACKEND
      type: 'group',
      logical: (rootServer.logical_operator ?? 'AND') as LogicalOp,
      children: attach(rootServer.id),
    };
  }

  // Se a raiz não é um grupo, criar um grupo wrapper
  console.log('📦 Criando wrapper para raiz não-grupo');
  return {
    id: `wrapper-${Date.now()}`, // ID temporário para wrapper
    type: 'group',
    logical: 'AND',
    children: attach(null),
  };
}

// Função auxiliar para extrair field_uuid se necessário
function extractFieldUuid(leftOperand: string | null): string | undefined {
  // Para submission.option_id, precisamos mapear o right_operand para o field_uuid
  // Isso será feito posteriormente quando carregarmos os campos
  return undefined;
}

// --------- Draft -> Body (criação do GERAL) ---------
export function draftToCreateBodyGeneral(
  d: RuleDraft,
  created_by: string,
  organization_uuid: string
): CreateRuleBodyGeneral {
  return {
    name: d.name,
    description: d.description,
    is_active: d.isActive,
    created_by,
    organization_uuid, // <- vem por argumento (não sofre undefined)
  };
}
